import type { RefObject } from "react"

import { safeExportUrl } from "./notebookExport"
import { replaceNotebookVideosForExport } from "./notebookMedia"
import { prepareNotebookSlideImages } from "./prepareNotebookSlideImages"

async function prepare<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  let abort: () => void = () => {}
  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        abort = () => reject(new DOMException("Export canceled", "AbortError"))
        signal.addEventListener("abort", abort, { once: true })
        timer = setTimeout(
          () =>
            reject(
              new Error(
                "The slides could not finish loading. Try exporting again.",
              ),
            ),
          10_000,
        )
        if (signal.aborted) {
          abort()
        }
      }),
    ])
  } finally {
    clearTimeout(timer)
    signal.removeEventListener("abort", abort)
  }
}

/** Capture the same full-size canvas used by Present, then fit each slide to a bounded 16:9 page. */
export async function captureNotebookSlides(
  ref: RefObject<HTMLDivElement | null>,
  count: number,
  signal: AbortSignal,
) {
  const deadline = Date.now() + 10_000
  let slides: HTMLElement[] = []
  let ready = false
  let previousCharts: string | undefined
  while (Date.now() < deadline) {
    signal.throwIfAborted()
    slides = Array.from(
      ref.current?.querySelectorAll<HTMLElement>("[data-pdf-slide]") ?? [],
    )
    const charts = slides.flatMap((slide) => [
      ...slide.querySelectorAll(".recharts-responsive-container"),
    ])
    const chartContent = charts.map((chart) => chart.innerHTML).join("")
    ready =
      count > 0 &&
      slides.length === count &&
      slides.every(
        (slide) =>
          slide.querySelector('[role="textbox"]') &&
          !slide.querySelector('[aria-busy="true"]'),
      ) &&
      charts.every(
        (chart) =>
          (chart.querySelector("svg.recharts-surface")?.getBoundingClientRect()
            .width ?? 0) > 0,
      ) &&
      (!charts.length || chartContent === previousCharts)
    if (ready) {
      break
    }
    previousCharts = chartContent
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 100)
    })
  }
  if (!ready) {
    throw new Error("The slides are still loading. Try exporting again.")
  }
  await prepare(document.fonts.ready, signal)
  slides.forEach(replaceNotebookVideosForExport)
  const imageWarnings = await prepareNotebookSlideImages(slides, signal)
  signal.throwIfAborted()
  const { getFontEmbedCSS, toCanvas } = await import("html-to-image")
  signal.throwIfAborted()
  const fontEmbedCSS = await prepare(getFontEmbedCSS(slides[0]), signal)
  const pages = []
  for (const slide of slides) {
    signal.throwIfAborted()
    const width = Math.max(1200, slide.scrollWidth)
    const height = Math.max(675, slide.scrollHeight)
    const scale = Math.min(1200 / width, 675 / height)
    const left = (1200 - width * scale) / 2
    const top = (675 - height * scale) / 2
    const background = window.getComputedStyle(
      slide.querySelector("article") ?? slide,
    ).backgroundColor
    const image = await prepare(
      toCanvas(slide, {
        width,
        height,
        canvasWidth: Math.max(1, Math.round(width * scale * 2)),
        canvasHeight: Math.max(1, Math.round(height * scale * 2)),
        pixelRatio: 1,
        fontEmbedCSS,
        includeQueryParams: true,
        fetchRequestInit: { referrerPolicy: "no-referrer" },
        filter: (node) =>
          !(
            node instanceof Element &&
            node.matches("[data-drag-handle], input[type=range]")
          ),
      }),
      signal,
    )
    signal.throwIfAborted()
    const canvas = document.createElement("canvas")
    canvas.width = 2400
    canvas.height = 1350
    const context = canvas.getContext("2d")
    if (!context) {
      throw new Error("Could not prepare the slide images. Try again.")
    }
    context.fillStyle = background
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, left * 2, top * 2)
    const bounds = slide.getBoundingClientRect()
    const links = [...slide.querySelectorAll("a[href]")].flatMap((link) => {
      const url = safeExportUrl(
        link.getAttribute("href") ?? "",
        window.location.origin,
      )
      if (!url) {
        return []
      }
      return [...link.getClientRects()].map((rect) => ({
        url,
        left: left + (rect.left - bounds.left) * scale,
        top: top + (rect.top - bounds.top) * scale,
        width: rect.width * scale,
        height: rect.height * scale,
      }))
    })
    pages.push({
      image: canvas.toDataURL("image/png"),
      links,
      warnings: imageWarnings[pages.length],
    })
  }
  return pages
}
